import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = anonymous, object = logged in
  const [user, setUser] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' | 'register'
  const exchangedRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const u = await api.me();
      setUser(u);
      return u;
    } catch {
      setUser(false);
      return null;
    }
  }, []);

  // Handle Emergent Google Auth callback (#session_id=… in URL fragment)
  useEffect(() => {
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    if (hash && hash.includes('session_id=')) {
      if (exchangedRef.current) return;
      exchangedRef.current = true;
      const match = hash.match(/session_id=([^&]+)/);
      const sid = match ? decodeURIComponent(match[1]) : null;
      // Strip the fragment so we don't re-process on reload
      window.history.replaceState(
        {}, '', window.location.pathname + window.location.search,
      );
      if (!sid) { refresh(); return; }
      (async () => {
        try {
          const res = await fetch(`${API}/auth/google/session`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'X-Session-ID': sid },
          });
          if (res.ok) {
            const u = await res.json();
            setUser(u);
            // Re-verify cookies actually stuck on this origin
            const verify = await fetch(`${API}/auth/me`, { credentials: 'include' });
            if (!verify.ok) {
              // Cookies didn't make it — diagnose loudly
              console.error('[AuthCallback] Session exchanged OK but /me returned',
                verify.status, '— cookies may be set on a different origin.');
              window.alert(
                'Google sign-in succeeded but the session cookie was rejected.\n\n' +
                "This usually means the backend URL (REACT_APP_BACKEND_URL) doesn't " +
                'match this site. Open the Network tab → look at the /api/auth/me ' +
                'response and the Set-Cookie on /api/auth/google/session — they ' +
                'should both be on this same domain.',
              );
              setUser(false);
            }
          } else {
            const errBody = await res.text().catch(() => '');
            console.error('[AuthCallback] /auth/google/session failed',
              res.status, errBody.slice(0, 200));
            window.alert(`Google sign-in failed (HTTP ${res.status}).\n${errBody.slice(0, 300)}`);
            await refresh();
          }
        } catch (err) {
          console.error('[AuthCallback] Network error during Google session exchange', err);
          window.alert('Could not reach the Sprite Vault backend.\n' + (err?.message || err));
          await refresh();
        }
      })();
    } else {
      refresh();
    }
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const u = await api.login({ email, password });
    setUser(u);
    return u;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const u = await api.register({ email, password, name });
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(async () => {
    try { await api.logout(); } catch { /* ignore */ }
    setUser(false);
  }, []);

  const openAuth = useCallback((mode = 'login') => {
    setAuthMode(mode);
    setAuthOpen(true);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthed: !!user && user !== false && user !== null,
        isAdmin: !!user && user !== false && user?.role === 'admin',
        loading: user === null,
        login, register, logout, refresh,
        authOpen, setAuthOpen, authMode, setAuthMode, openAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
